/**
 * Touch input overlay (sc-3289 phase 2).
 *
 * Renders an on-screen virtual joystick (bottom-left) + per-cartridge
 * action buttons (bottom-right) during cartridge play on touch devices.
 * Pointer events on the overlay call `pressButton(btn)` /
 * `releaseButton(btn)` / `setStickL(x, y)` on the shared keyboard input
 * source — cartridges see a single unified `InputState` and don't need
 * to know whether a press came from a real key or a virtual button.
 *
 * **Joystick:** a fixed circular base + a knob the player drags from
 * center. The knob's offset (clamped to the base radius) becomes:
 *   - `stickL.x / stickL.y` floats in [-1, 1] (analog-aware cartridges)
 *   - Digital `up`/`down`/`left`/`right` once the magnitude clears a
 *     small deadzone (~22%) — same booleans an Arrow keypress would set.
 * Springs back to center on release.
 *
 * **Action buttons:** rendered from the cartridge's `touch` map. Each
 * declared button shows its short label ("ACCEL", "BRAKE", "TURBO",
 * "JUMP", etc.). Undeclared buttons are not rendered — cartridges that
 * only need fireA show one button, not three. The START button is
 * always present (every cartridge can use START during transitions).
 *
 * Visibility:
 *   - Auto-visible if the host is a touch device (`isTouch()` true at
 *     attach time).
 *   - Toggleable at runtime by pressing **T** anywhere (desktop testing).
 *
 * Lifecycle:
 *   - `attachTouchControls({ input, module_ })` once per cartridge run.
 *   - `setTouchControlsVisible(visible)` during gameplay/non-gameplay.
 *   - `detachTouchControls()` at cartridge teardown.
 */

import type { GameModule, InputButton } from '@arcraz/arcadia-types';
import type { KeyboardInputSource } from './input-keyboard.js';
import { isTouch } from './context.js';

interface TouchControlsOpts {
  input: KeyboardInputSource;
  module_: GameModule;
}

const ROOT_ID = 'touch-controls';

/** Magnitude (0..1) below which the joystick is treated as centered. */
const JOY_DEADZONE = 0.22;

/** Angle slop for digital button decisions — buttons turn on within
 *  ±67.5° of cardinal direction so adjacent diagonals trigger both
 *  buttons (NW corner → up+left). */
const JOY_DIAGONAL_SLOP = Math.PI * (67.5 / 180);

interface ActionButtonSpec {
  btn: InputButton;
  label: string;
}

let state: {
  input: KeyboardInputSource;
  root: HTMLDivElement;
  /** Joystick base + knob elements. */
  joyBase: HTMLDivElement;
  joyKnob: HTMLDivElement;
  /** Map of action button element → InputButton. */
  actionButtons: Map<HTMLElement, InputButton>;
  /** Currently-held digital buttons set BY the joystick (so we can
   *  release exactly the ones we pressed on the next move). */
  joyHeld: Set<InputButton>;
  /** Joystick state. */
  joyPointerId: number | null;
  joyCenterX: number;
  joyCenterY: number;
  joyRadius: number;
  /** Pointer-id → action button currently held by that pointer. */
  actionPointerOwner: Map<number, HTMLElement>;
  /** Window listeners we own (for cleanup). */
  joyMoveListener: (e: PointerEvent) => void;
  joyUpListener: (e: PointerEvent) => void;
  globalUpListener: (e: PointerEvent) => void;
  toggleListener: (e: KeyboardEvent) => void;
  manualOverride: boolean;
  visible: boolean;
} | null = null;

export function attachTouchControls(opts: TouchControlsOpts): void {
  detachTouchControls();

  const root = ensureRoot();
  const joyBase = ensureJoystick(root);
  const joyKnob = joyBase.firstElementChild as HTMLDivElement;
  const actionsContainer = ensureActionsContainer(root);
  populateActionButtons(actionsContainer, opts.module_);

  const actionButtons = new Map<HTMLElement, InputButton>();
  actionsContainer.querySelectorAll<HTMLElement>('[data-touch-btn]').forEach((el) => {
    actionButtons.set(el, el.dataset.touchBtn as InputButton);
  });

  const actionPointerOwner = new Map<number, HTMLElement>();
  const joyHeld = new Set<InputButton>();

  const releaseActionFor = (pointerId: number, evt?: PointerEvent): void => {
    const el = actionPointerOwner.get(pointerId);
    if (!el) return;
    actionPointerOwner.delete(pointerId);
    el.classList.remove('is-pressed');
    const btn = state?.actionButtons.get(el);
    if (btn) state?.input.releaseButton(btn);
    if (evt) evt.preventDefault();
  };

  const handleActionDown = (evt: PointerEvent): void => {
    const target = evt.target;
    if (!(target instanceof HTMLElement)) return;
    const btn = actionButtons.get(target);
    if (!btn) return;
    evt.preventDefault();
    if ('setPointerCapture' in target) {
      try {
        target.setPointerCapture(evt.pointerId);
      } catch {
        // Ignore.
      }
    }
    actionPointerOwner.set(evt.pointerId, target);
    target.classList.add('is-pressed');
    state?.input.pressButton(btn);
  };

  const handleActionUp = (evt: PointerEvent): void => releaseActionFor(evt.pointerId, evt);
  const handleActionLeave = (evt: PointerEvent): void => releaseActionFor(evt.pointerId, evt);

  for (const el of actionButtons.keys()) {
    el.addEventListener('pointerdown', handleActionDown);
    el.addEventListener('pointerup', handleActionUp);
    el.addEventListener('pointercancel', handleActionUp);
    el.addEventListener('pointerleave', handleActionLeave);
  }

  // ---- Joystick wiring ----
  const handleJoyDown = (evt: PointerEvent): void => {
    if (!state) return;
    if (state.joyPointerId !== null) return; // already owned by another pointer
    evt.preventDefault();
    if ('setPointerCapture' in joyBase) {
      try {
        joyBase.setPointerCapture(evt.pointerId);
      } catch {
        // Ignore.
      }
    }
    const rect = joyBase.getBoundingClientRect();
    state.joyCenterX = rect.left + rect.width / 2;
    state.joyCenterY = rect.top + rect.height / 2;
    state.joyRadius = rect.width / 2;
    state.joyPointerId = evt.pointerId;
    joyBase.classList.add('is-active');
    updateJoystickFromPointer(evt.clientX, evt.clientY);
  };
  joyBase.addEventListener('pointerdown', handleJoyDown);

  const joyMoveListener = (evt: PointerEvent): void => {
    if (!state || state.joyPointerId !== evt.pointerId) return;
    evt.preventDefault();
    updateJoystickFromPointer(evt.clientX, evt.clientY);
  };

  const joyUpListener = (evt: PointerEvent): void => {
    if (!state || state.joyPointerId !== evt.pointerId) return;
    evt.preventDefault();
    state.joyPointerId = null;
    joyBase.classList.remove('is-active');
    recenterJoystick();
  };

  window.addEventListener('pointermove', joyMoveListener);
  window.addEventListener('pointerup', joyUpListener);
  window.addEventListener('pointercancel', joyUpListener);

  // Belt-and-suspenders: drop held action buttons if a pointerup is missed.
  const globalUpListener = (evt: PointerEvent): void => {
    if (actionPointerOwner.has(evt.pointerId)) {
      releaseActionFor(evt.pointerId);
    }
  };
  window.addEventListener('pointerup', globalUpListener);
  window.addEventListener('pointercancel', globalUpListener);

  const toggleListener = (evt: KeyboardEvent): void => {
    if (evt.code !== 'KeyT') return;
    if (
      evt.target instanceof HTMLElement &&
      (evt.target.tagName === 'INPUT' ||
        evt.target.tagName === 'TEXTAREA' ||
        evt.target.isContentEditable)
    ) {
      return;
    }
    if (!state) return;
    state.manualOverride = !state.manualOverride;
    applyVisibility();
  };
  window.addEventListener('keydown', toggleListener);

  state = {
    input: opts.input,
    root,
    joyBase,
    joyKnob,
    actionButtons,
    joyHeld,
    joyPointerId: null,
    joyCenterX: 0,
    joyCenterY: 0,
    joyRadius: 0,
    actionPointerOwner,
    joyMoveListener,
    joyUpListener,
    globalUpListener,
    toggleListener,
    manualOverride: false,
    visible: false,
  };
  applyVisibility();
}

export function detachTouchControls(): void {
  if (!state) return;
  for (const el of state.actionPointerOwner.values()) {
    const btn = state.actionButtons.get(el);
    if (btn) state.input.releaseButton(btn);
    el.classList.remove('is-pressed');
  }
  state.actionPointerOwner.clear();
  for (const btn of state.joyHeld) {
    state.input.releaseButton(btn);
  }
  state.joyHeld.clear();
  state.input.setStickL(0, 0);
  recenterKnob(state.joyKnob);

  window.removeEventListener('pointermove', state.joyMoveListener);
  window.removeEventListener('pointerup', state.joyUpListener);
  window.removeEventListener('pointercancel', state.joyUpListener);
  window.removeEventListener('pointerup', state.globalUpListener);
  window.removeEventListener('pointercancel', state.globalUpListener);
  window.removeEventListener('keydown', state.toggleListener);

  state.root.hidden = true;
  state = null;
}

export function setTouchControlsVisible(visible: boolean): void {
  if (!state) return;
  state.visible = visible;
  applyVisibility();
}

function applyVisibility(): void {
  if (!state) return;
  const shouldShow = state.visible && (isTouch() || state.manualOverride);
  state.root.hidden = !shouldShow;
}

// ============================================================================
// Joystick math + visual state
// ============================================================================

function updateJoystickFromPointer(clientX: number, clientY: number): void {
  if (!state) return;
  const dx = clientX - state.joyCenterX;
  const dy = clientY - state.joyCenterY;
  const radius = state.joyRadius;
  const dist = Math.hypot(dx, dy);
  // Clamp knob to base circle.
  let nx = dx / radius;
  let ny = dy / radius;
  if (dist > radius) {
    nx = dx / dist;
    ny = dy / dist;
  }

  // Visual: move the knob to (nx*radius, ny*radius) from base center.
  const knobOffsetPx = radius * 0.65; // knob center can travel within 65% of base radius
  state.joyKnob.style.transform =
    `translate(calc(-50% + ${nx * knobOffsetPx}px), calc(-50% + ${ny * knobOffsetPx}px))`;

  // Analog: emit stick value (clamped magnitude already to <=1).
  state.input.setStickL(nx, ny);

  // Digital cardinals: gated by deadzone, then bucketed by angle.
  const mag = Math.hypot(nx, ny);
  const newDirs = new Set<InputButton>();
  if (mag >= JOY_DEADZONE) {
    const ang = Math.atan2(ny, nx); // -π..π; 0 = +x (right), π/2 = +y (down)
    // RIGHT around angle 0; LEFT around ±π; UP around -π/2; DOWN around π/2.
    if (Math.abs(ang) <= JOY_DIAGONAL_SLOP) newDirs.add('right');
    if (Math.abs(Math.PI - Math.abs(ang)) <= JOY_DIAGONAL_SLOP) newDirs.add('left');
    if (Math.abs(ang + Math.PI / 2) <= JOY_DIAGONAL_SLOP) newDirs.add('up');
    if (Math.abs(ang - Math.PI / 2) <= JOY_DIAGONAL_SLOP) newDirs.add('down');
  }

  // Release directions we previously held but no longer do.
  for (const dir of state.joyHeld) {
    if (!newDirs.has(dir)) {
      state.input.releaseButton(dir);
    }
  }
  // Press newly-held directions.
  for (const dir of newDirs) {
    if (!state.joyHeld.has(dir)) {
      state.input.pressButton(dir);
    }
  }
  state.joyHeld = newDirs;
}

function recenterJoystick(): void {
  if (!state) return;
  // Release any digital direction the stick was setting.
  for (const dir of state.joyHeld) {
    state.input.releaseButton(dir);
  }
  state.joyHeld.clear();
  state.input.setStickL(0, 0);
  recenterKnob(state.joyKnob);
}

function recenterKnob(knob: HTMLElement): void {
  knob.style.transform = 'translate(-50%, -50%)';
}

// ============================================================================
// DOM construction
// ============================================================================

function ensureRoot(): HTMLDivElement {
  let root = document.getElementById(ROOT_ID) as HTMLDivElement | null;
  if (root) {
    if (root.parentElement !== document.body) {
      document.body.appendChild(root);
    }
    // Wipe previous structure — the action button set may differ per cartridge.
    while (root.firstChild) root.removeChild(root.firstChild);
    return root;
  }
  root = document.createElement('div');
  root.id = ROOT_ID;
  root.className = 'touch-controls';
  root.setAttribute('aria-hidden', 'true');
  root.hidden = true;
  document.body.appendChild(root);
  return root;
}

function ensureJoystick(root: HTMLDivElement): HTMLDivElement {
  const base = document.createElement('div');
  base.className = 'touch-joy';
  base.setAttribute('aria-label', 'Joystick');
  base.style.touchAction = 'none';
  base.style.userSelect = 'none';
  (base.style as CSSStyleDeclaration & { webkitUserSelect: string }).webkitUserSelect = 'none';

  const knob = document.createElement('div');
  knob.className = 'touch-joy__knob';
  recenterKnob(knob);
  base.appendChild(knob);

  root.appendChild(base);
  return base;
}

function ensureActionsContainer(root: HTMLDivElement): HTMLDivElement {
  const container = document.createElement('div');
  container.className = 'touch-action';
  root.appendChild(container);
  return container;
}

function populateActionButtons(
  container: HTMLDivElement,
  module_: GameModule
): void {
  const allSpecs = buildActionSpecs(module_);
  // Split START off — primary buttons sit on the arc; START gets its own
  // quiet placement below the arc.
  const primary = allSpecs.filter((s) => s.btn !== 'start');
  const start = allSpecs.find((s) => s.btn === 'start');

  // Distribute primary buttons along the 9 o'clock → 12 o'clock arc,
  // centered at 10:30 (225°). Fixed gap between adjacent buttons keeps
  // them visually grouped — endpoints-spread looks too spaced out for
  // 2 buttons and overlaps for higher N. Layouts:
  //   N=1: 225°           (center of arc)
  //   N=2: 205°, 245°     (±20° from center — clustered around 10:30)
  //   N=3: 190°, 225°, 260° (±35°, 0, +35°)
  //
  // With the arc radius below (~130px on tablets), N=2 gives ~89px chord
  // between centers — comfortable separation for 72px diameter circles.
  const N = primary.length;
  const gapDeg = N <= 2 ? 40 : 35; // tighter spacing as count grows
  primary.forEach((spec, i) => {
    const centeredIndex = i - (N - 1) / 2;
    const angleDeg = 225 + centeredIndex * gapDeg;
    const el = makeActionButton(spec);
    el.style.setProperty('--touch-arc-angle', `${angleDeg}deg`);
    container.appendChild(el);
  });

  if (start) {
    const el = makeActionButton(start);
    container.appendChild(el);
  }
}

function makeActionButton(spec: ActionButtonSpec): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className =
    spec.btn === 'start'
      ? 'touch-action__btn touch-action__btn--start'
      : 'touch-action__btn';
  el.dataset.touchBtn = spec.btn;
  el.setAttribute('aria-label', spec.label || spec.btn);
  el.textContent = spec.label;
  el.style.touchAction = 'none';
  el.style.userSelect = 'none';
  (el.style as CSSStyleDeclaration & { webkitUserSelect: string }).webkitUserSelect = 'none';
  return el;
}

function buildActionSpecs(module_: GameModule): ActionButtonSpec[] {
  const specs: ActionButtonSpec[] = [];
  // Fixed order so the layout is consistent across cartridges.
  const orderedFireButtons: InputButton[] = ['fireA', 'fireB', 'fireC'];
  for (const btn of orderedFireButtons) {
    const label = module_.touch?.[btn];
    if (label) specs.push({ btn, label });
  }
  // START is opt-in. Most cartridges don't need an in-game START button
  // — press-start, initials, and pause overlays handle their own taps.
  // Cartridges that DO need a runtime START button declare touch.start.
  const startLabel = module_.touch?.start;
  if (startLabel) specs.push({ btn: 'start', label: startLabel });
  return specs;
}
